import { Injectable, OnDestroy } from '@angular/core';
import { isNativeMobile } from 'src/app/shared/utils/platform.utils';
import { NativeAudio } from '@capacitor-community/native-audio';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, map, Subscription } from 'rxjs';
import { AppState } from 'src/app/core/state/app.state';

const SOUNDS = {
    'fold': 'assets/sounds/game/fold.wav',
    'start': 'assets/sounds/game/start-turn.wav',
    'wager': 'assets/sounds/game/wager.wav',
    'warning': 'assets/sounds/game/warning.wav',
    'win': 'assets/sounds/game/win.wav',
    'drip': `assets/sounds/drip_drop.wav`,
    'join': `assets/sounds/join.wav`,
    'leave': 'assets/sounds/leave.wav',
    'poop': 'assets/sounds/emotes-sound/poop.wav',
    'beer': 'assets/sounds/emotes-sound/beer.wav',
    'tomato': 'assets/sounds/emotes-sound/tomato.wav',
    'match-stick': 'assets/sounds/emotes-sound/match-stick.wav',
    'all-in': 'assets/sounds/all-in.wav',
    'bet-call': 'assets/sounds/bet-call.wav',
    'check': 'assets/sounds/check.wav',
    'community-card': 'assets/sounds/community-card.wav',
    'shop': 'assets/sounds/shop.wav',
    'shuffle': 'assets/sounds/shuffle.wav',
    'super-spin': 'assets/sounds/super-spin.mp3',
    'angry-cursing': 'assets/sounds/emotes-sound/angry-cursing.wav',
    'face-kiss': 'assets/sounds/emotes-sound/face-kiss.wav',
    'mind-blast': 'assets/sounds/emotes-sound/mind-blast.wav',
    'puke-throw': 'assets/sounds/emotes-sound/puke-throw.wav',
    'tears-LOL': 'assets/sounds/emotes-sound/tears-LOL.wav',
    'sad-cry': 'assets/sounds/emotes-sound/sad-cry.wav',
    'targ_hit': 'assets/sounds/emotes-sound/targ_hit.wav',
    'sopo-transition-open': 'assets/sounds/sopo_transition/transition_open.wav',
    'sopo-transition-close': 'assets/sounds/sopo_transition/transition_close.wav',
    'gold-purchase': 'assets/sounds/gold-purchase.mp3',
    'sweepsbuck-purchase': 'assets/sounds/sweepsbuck-purchase.mp3',
}

const REPLAYABLE_ASSETS: Set<Sound> = new Set([
    'shop',
    'gold-purchase',
    'sweepsbuck-purchase',
    'poop',
    'beer',
    'tomato',
    'match-stick',
    'angry-cursing',
    'face-kiss',
    'mind-blast',
    'puke-throw',
    'tears-LOL',
    'sad-cry',
    'targ_hit',
]);

const MAX_NUMBER_OF_ATTEMPTS = 10;
const UNLOAD_AND_PRELOAD = 5;
const NUMBER_OF_IS_PLAYING_CHECKS_PER_ATTEMPTS = 3;
const PLAY_START_DELAY_MS = 150;
const IS_PLAYING_CHECK_INTERVAL_MS = 50;

@Injectable({
    providedIn: 'root',
})
export class SmartAudioService implements OnDestroy {
    private _inProgressSound: { [index: string]: number } = {};
    private webAudioElements = new Map<string, HTMLAudioElement>();
    gameThemeSettingsSubscription: Subscription;
    muteSounds: boolean = false;
    appIsActive: boolean = true;

    constructor(
        private store: Store<AppState>,
    ) {
        if(isNativeMobile()) NativeAudio.configure({focus: false});
        this.init();
    }

    init() {
        this.gameThemeSettingsSubscription = this.store.select(state => state.gameThemeSetting).pipe(
            map(settings => settings.muteSounds),
            distinctUntilChanged(),
        ).subscribe(muteSounds => {
            this.muteSounds = muteSounds;
            if (muteSounds) this.stopAllSounds();
        });
    }

    async load(): Promise<void> {
        if (isNativeMobile()) {
            await Promise.all(
                Object.entries(SOUNDS).map(([key, path]) => this.loadSound(key, path))
            );
        } else {
            await Promise.all(
                Object.entries(SOUNDS).map(([key, path]) => this.loadWebAudio(key, path))
            );
        }
        console.debug('[SOUND] All sounds preloaded');
    }

    private async loadWebAudio(key: string, relativePath: string): Promise<void> {
        try {
            const response = await fetch(relativePath);
            if (!response.ok) throw new Error(`HTTP ${response.status} ${response.statusText} - ${response.url}`);
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            const audio = new Audio(blobUrl);
            this.webAudioElements.set(key, audio);
        } catch (e) {
            console.error(`[SOUND] Failed to load ${key}:`, e);
        }
    }

    private async loadSound(key: string, audioSrcPath: string) {
        try {
            await NativeAudio.preload({
                assetPath: isNativeMobile() ? 'public/' + audioSrcPath : audioSrcPath,
                assetId: key,
            });
        } catch (e) {
            const msg = String(e?.message || e);
            if (msg.includes('already exists') || msg.includes('already loaded')) {
                console.warn(`(Warning) Sound ${key} is already preloaded:`, e);
            } else {
                console.error(`Error loading sound ${key}. Reason: `, e);
            }
        }
    }

    private async isPlaying(sound: string): Promise<IPlaying> {
        try {
            const isPlaying = await NativeAudio.isPlaying({assetId: sound});
            return { isPlaying: isPlaying.isPlaying, missingAudio: false };
        } catch (error) {
            console.error(sound + ' sound missing! Original error: ', error);
            return { isPlaying: false, missingAudio: true };
        }
    }

    public async playSounds(sound: Sound): Promise<void> {
        console.debug(`[SOUND] - sound: ${sound} - appIsActive: ${this.appIsActive} - muteSounds: ${this.muteSounds}`);
        if (!this.appIsActive) return;
        if (this.muteSounds) return;

        if (!isNativeMobile()) {
            this.playWebAudio(sound);
            return;
        }

        const isReplayable = REPLAYABLE_ASSETS.has(sound);
        if (!isReplayable && this._inProgressSound[sound] !== undefined) return;

        if (isReplayable && this._inProgressSound[sound] !== undefined) {
            const newAssetId = `${sound}_${Date.now()}`;
            this._inProgressSound[newAssetId] = 1;
            this.playSound(newAssetId, true);
            console.debug(`[SOUND] - replayable asset: ${sound} - newAssetId: ${newAssetId}`);
            return;
        }

        this._inProgressSound[sound] = 1;
        this.playSound(sound);
    }

    private playWebAudio(sound: Sound) {
        const isReplayable = REPLAYABLE_ASSETS.has(sound);
        const existing = this.webAudioElements.get(sound);

        if (!existing) {
            console.error(`[SOUND] Not preloaded: ${sound}`);
            return;
        }

        if (isReplayable) {
            // Clone so overlapping plays don't interrupt each other
            const clone = existing.cloneNode() as HTMLAudioElement;
            clone.play().catch(e => {
                if (e.name === 'NotAllowedError') return;
                console.error(`[SOUND] Error playing ${sound}:`, e);
            });
            return;
        }

        if (!existing.paused) return; // already playing, skip
        existing.currentTime = 0;
        existing.play().catch(e => {
            if (e.name === 'NotAllowedError') return;
            console.error(`[SOUND] Error playing ${sound}:`, e);
        });
    }

    private async playSound(assetId: string, newAssetId = false) {
        if (this.muteSounds) {
            if (newAssetId) this.unloadReplayableAsset(assetId);
            delete this._inProgressSound[assetId];
            return;
        }

        const numberOfAttempts = this._inProgressSound[assetId];

        if (newAssetId && numberOfAttempts === 1) {
            try {
                const sound = assetId.replace(/_\d+$/, '');
                console.debug(`[SOUND] - needs preload assetId: ${assetId} - sound: ${sound}`);
                await this.preload(assetId, sound);
            } catch (error) {
                console.error(`Error preloading replayable assetId (${ assetId }), sound: ${ assetId.replace(/_\d+$/, '') }. Reason: `, error);
                delete this._inProgressSound[assetId];
                return;
            }
        }

        if (numberOfAttempts > 1) { // checking the playing status only on the second call
            for (let i = 1; i <= NUMBER_OF_IS_PLAYING_CHECKS_PER_ATTEMPTS; i++) { // triple playing check
                if (i > 1) await new Promise(resolve => setTimeout(resolve, IS_PLAYING_CHECK_INTERVAL_MS));
                const playing = await this.isPlaying(assetId);

                if (playing.isPlaying || playing.missingAudio) {
                    if (playing.isPlaying && numberOfAttempts === UNLOAD_AND_PRELOAD + 1) {
                        console.debug('[Sound]  (Fix issue) Max number of attempts. Unload and preload!');
                    }

                    let soundDuration = 0;
                    if (playing.isPlaying) {
                        try {
                            soundDuration = (await NativeAudio.getDuration({assetId: assetId}))?.duration * 1000;
                        } catch(error) {
                            console.error(`Error getting duration with assetId (${ assetId }). Reason: `, error);
                        }
                        soundDuration = newAssetId ? soundDuration + 500 : soundDuration + 50;
                        setTimeout(() => {
                            if (newAssetId) this.unloadReplayableAsset(assetId);
                            delete this._inProgressSound[assetId];
                        }, soundDuration);
                        return;
                    }
                    delete this._inProgressSound[assetId];
                    return;
                }
            }
        }

        if (numberOfAttempts >= MAX_NUMBER_OF_ATTEMPTS) {
            console.error(`Has reached the maximum number of attempts! Sound assetId = ${assetId}`);
            if (newAssetId) this.unloadReplayableAsset(assetId);
            delete this._inProgressSound[assetId];
            return;
        }

        if (numberOfAttempts === UNLOAD_AND_PRELOAD) {
            console.debug(`[Sound] - Number of attempts (${UNLOAD_AND_PRELOAD}). Unload assetId(${assetId}) and preload it before the next play attempt!`);
            if (newAssetId) {
                const sound = assetId.replace(/_\d+$/, '');
                await this.unloadAndPreload(assetId, sound);
            } else {
                await this.unloadAndPreload(assetId);
            }
        }

        NativeAudio.play({
            assetId: assetId,
            time: 0
        })
        .then(() => {
            ++this._inProgressSound[assetId];
            // Delay before checking isPlaying — on iOS, .then() fires before AVAudioPlayer actually starts
            setTimeout(() => this.playSound(assetId, newAssetId), PLAY_START_DELAY_MS);
        })
        .catch((err) => {
            console.error(`Error playing sound ${ assetId }. Reason: `, err);
            if (newAssetId) this.unloadReplayableAsset(assetId);
            delete this._inProgressSound[assetId];
        });
    }

    public setIsActive(isActive: boolean) {
        this.appIsActive = isActive;
    }

    private stopAllSounds() {
        const inProgressIds = Object.keys(this._inProgressSound);
        for (const assetId of inProgressIds) {
            const isReplayableClone = /^.+_\d+$/.test(assetId);
            NativeAudio.stop({ assetId }).then(() => {
                if (isReplayableClone) this.unloadReplayableAsset(assetId);
            }).catch((err) => { console.error(`Error stopping sound ${assetId}:`, err); });
            delete this._inProgressSound[assetId];
        }
    }

    private async unloadReplayableAsset(assetId: string) {
        try {
            await this.unload(assetId);
        } catch (error) {
            console.error(`Rejected unloading replayable assetId: ${ assetId }. Reason:`, error);
        }
    }

    private async unloadAndPreload(assetId: string, sound?: string): Promise<void> {
        try {
            await this.unload(assetId);
            await this.preload(assetId, sound);
        } catch (error) {
            console.error(`Rejected assetId: ${ assetId }. Reason:`, error);
        }
    }

    private async preload(assetId: string, sound?: string): Promise<void> {
        try {
            await NativeAudio.preload({
                assetId: assetId,
                assetPath: isNativeMobile() ? 'public/' + SOUNDS[sound ? sound : assetId] : SOUNDS[sound ? sound : assetId],
            });
        } catch (e) {
            const msg = String(e?.message || e);
            if (msg.includes('already exists') || msg.includes('already loaded')) {
                console.warn(`(Warning) Sound ${assetId} is already preloaded:`, e);
            } else {
                console.error(`Error loading sound ${assetId}. Reason: `, e);
            }
        }
    }

    private async unload(assetId: string): Promise<void> {
        await NativeAudio.unload({
            assetId: assetId,
        });
    }

    ngOnDestroy() {
        this.gameThemeSettingsSubscription.unsubscribe();
        this.webAudioElements.forEach(audio => {
            URL.revokeObjectURL(audio.src);
        });
        this.webAudioElements.clear();
    }
}

export type Sound = keyof typeof SOUNDS;

interface IPlaying {
    isPlaying: boolean;
    missingAudio: boolean;
}
